import React, {
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import { IconSize } from "@/src/components/icons/components/IconContainer";
import { Popover } from "@headlessui/react";
import { Card } from "@/src/components/cards/components/Card";
import {
  CardBorderTheme,
  CardTheme,
} from "@/src/components/cards/components/Card/types";
import { Close } from "nvs-ds-icons/react/navigation";
import { useIsMobile } from "@/libs/utils";
import TooltipButton from "./TooltipButton";
import clsx from "clsx";
import { useWindowSize, useIsomorphicLayoutEffect } from "@react-hookz/web";

export type TooltipPanelProps = PropsWithChildren<{
  header: ReactNode;
  content: ReactNode;
  theme?: CardTheme;
  borderTheme?: CardBorderTheme;
  className?: string;
}>;

const TooltipPanel: React.FC<TooltipPanelProps> = ({
  header,
  content,
  className,
  theme = CardTheme.dark,
  borderTheme = CardBorderTheme.light,
}) => {
  const isMobile = useIsMobile();
  const windowResize = useWindowSize();
  const [panelRef, setPanelRef] = useState<HTMLDivElement | null>(null);

  const resetPosition = useCallback(() => {
    if (panelRef !== null) {
      panelRef.style.transform = "none";
    }
  }, [panelRef]);

  const responsivePanelPosition = useCallback(() => {
    if (panelRef !== null) {
      resetPosition();

      const panelRect = panelRef.getBoundingClientRect();

      const sitePadding = `var(--site-padding-${
        isMobile ? "mobile" : "laptop"
      })`;

      const panelLeftX = panelRect.left;
      const panelRightX = panelRect.right;

      const overflowsLeft = panelLeftX < 0;
      const overflowsRight = panelRightX > window.innerWidth;

      if (overflowsLeft) {
        const translateX = panelLeftX * -1;
        panelRef.style.transform = `translateX(calc(${translateX}px + ${sitePadding}))`;
      } else if (overflowsRight) {
        const translateX = window.innerWidth - panelRightX;
        panelRef.style.transform = `translateX(calc(${translateX}px - ${sitePadding}))`;
      }
    }
  }, [isMobile, panelRef, resetPosition]);

  useIsomorphicLayoutEffect(() => {
    responsivePanelPosition();

    return () => {
      resetPosition();
    };
  }, [responsivePanelPosition, resetPosition, windowResize]);

  return (
    <Popover.Panel
      ref={setPanelRef}
      className="tablet:-left-12 tablet:top-full absolute right-0 bottom-full my-4 h-fit w-fit z-tooltip"
      role="tooltip"
    >
      <Card
        hasBorder
        theme={theme}
        borderTheme={borderTheme}
        className={clsx(`w-72 p-4`, className)}
      >
        <article>
          <div className="mb-xs flex h-fit w-full items-center justify-between">
            {header}
            <TooltipButton
              label="Close tooltip"
              size={IconSize.xs}
              icon={<Close />}
            />
          </div>

          {content}
        </article>
      </Card>
    </Popover.Panel>
  );
};

export default TooltipPanel;
